/**
 * In-memory backing store for the onboarding sample trip.
 *
 * lib/api/trips.ts routes every read/write for DEMO_TRIP_ID here instead
 * of Firestore, so the tutorial is fully hands-on without persisting
 * anything. State is module-scoped: it survives navigation, is gone on
 * reload, and is rebuilt from scratch each time the tour (re)starts.
 *
 * Updates are immutable (new objects each time) so TanStack Query sees
 * fresh references when it refetches after a mutation.
 */

import type { Activity, Trip } from "@travelix/core";

import {
  buildDemoActivities,
  buildDemoTrip,
  type DemoOwner,
} from "./demo-trip";

/** Things the user can do in the sample trip that the tour listens for. */
export type DemoEvent =
  | "activity:created"
  | "expense:added"
  | "poll:voted"
  | "packing:claimed"
  | "idea:added";

interface DemoState {
  trip: Trip;
  activities: Activity[];
}

let state: DemoState | null = null;
/** Id of the last activity the user added — the tour points at it. */
let lastCreatedActivityId: string | null = null;
const listeners = new Set<(event: DemoEvent) => void>();

/** (Re)build the sample trip. Called when the tour starts. */
export function resetDemo(owner: DemoOwner): void {
  state = { trip: buildDemoTrip(owner), activities: buildDemoActivities(owner) };
  lastCreatedActivityId = null;
}

/** Lazily build the sample if a demo route is hit before the tour seeded it. */
export function ensureDemo(owner: DemoOwner): DemoState {
  if (!state) resetDemo(owner);
  return state!;
}

export function clearDemo(): void {
  state = null;
  lastCreatedActivityId = null;
}

export function getLastCreatedActivityId(): string | null {
  return lastCreatedActivityId;
}

export function setLastCreatedActivityId(id: string): void {
  lastCreatedActivityId = id;
}

export function getDemoState(): DemoState | null {
  return state;
}

export function updateDemoTrip(fn: (trip: Trip) => Trip): void {
  if (!state) return;
  state = { ...state, trip: fn(state.trip) };
}

export function updateDemoActivities(fn: (activities: Activity[]) => Activity[]): void {
  if (!state) return;
  state = { ...state, activities: fn(state.activities) };
}

export function emitDemoEvent(event: DemoEvent): void {
  for (const l of listeners) l(event);
}

export function onDemoEvent(listener: (event: DemoEvent) => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}
